import type { ClientPollCreateRequest } from '@DigitaleDoerfer/digitale-doerfer-api';
import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { KeyboardController } from 'react-native-keyboard-controller';
import { IconButton, Text, TextInput } from 'react-native-paper';

import { AttachmentIcon, CameraIcon, CloseIcon, PollIcon } from '@/assets';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import { AttachmentList } from '@/features/media/components/AttachmentList';
import { MediaItemPlaceholderList } from '@/features/media/components/MediaItemPlaceholderList';
import { MediaMenu } from '@/features/media/components/menu/MediaMenu';
import type {
  DocumentItemPlaceholder,
  ImagePickType,
  MediaItemPlaceholder
} from '@/features/media/types/picker';
import { PollPreview } from '@/features/post-editor/components/poll/PollPreview';
import type { Poll } from '@/features/post-editor/types/poll';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { hasItems } from '@/shared/utils/array';
import { isAndroid } from '@/shared/utils/platform';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: { backgroundColor: theme.colors.surfaceContainerLowest },
    textInput: {
      fontSize: 18,
      paddingTop: isAndroid ? 16 : 0,
      minHeight: 80,
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    imageGrid: { backgroundColor: theme.colors.surfaceContainerLowest },
    editorFooter: { height: 64, padding: 12, flexDirection: 'row', gap: 12 },
    counter: {
      color: theme.colors.onSurfaceVariant,
      alignSelf: 'flex-end',
      marginRight: 'auto'
    },
    iconButton: { margin: 0 }
  });

type PostContentEditorProps = {
  text: string;
  placeholder: string;
  limit: number;
  onTextChange: (text: string) => void;
  images: MediaItemPlaceholder[];
  maxImages?: number;
  isImageUploading?: boolean;
  onDeleteImage: (image: MediaItemPlaceholder) => void;
  onPickImage: (pick: ImagePickType) => void;
  onReorderImages?: (reordered: MediaItemPlaceholder[]) => void;
  documents: DocumentItemPlaceholder[];
  maxDocuments?: number;
  isDocumentUploading?: boolean;
  onDeleteDocument: (document: DocumentItemPlaceholder) => void;
  onPickDocument: () => Promise<void>;
  poll?: Poll;
  onPollChange: (poll?: ClientPollCreateRequest) => void;
  onPollDelete: (id?: string) => void;
};

export const PostContentEditor: FC<PostContentEditorProps> = ({
  text = '',
  placeholder = '',
  limit = 360,
  onTextChange,
  images,
  maxImages = 10,
  isImageUploading = false,
  onDeleteImage,
  onPickImage,
  onReorderImages,
  documents,
  maxDocuments = 5,
  isDocumentUploading = false,
  onDeleteDocument,
  onPickDocument,
  poll,
  onPollChange,
  onPollDelete
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.POST_EDITOR, {
    keyPrefix: 'screens.post-editor'
  });

  const { isFeatureEnabled } = useFeature();

  const [showImageMenu, setShowImageMenu] = useState(false);

  const [showPollEdit, setShowPollEdit] = useState<boolean>(false);
  const handlePollEdit = useCallback((show: boolean) => {
    KeyboardController.dismiss();
    setShowPollEdit(show);
  }, []);

  const { height } = useWindowDimensions();

  const canPickMoreImages = images.length < maxImages;
  const canPickMoreAttachments = documents.length < maxDocuments;

  return (
    <View style={styles.container}>
      <TextInput
        // oxlint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        style={[styles.textInput, { maxHeight: height * 0.25 }]}
        accessibilityLabel={t('accessibility.editorLabel')}
        accessibilityHint={t('accessibility.editorHint')}
        multiline
        mode="outlined"
        placeholder={placeholder}
        outlineColor="transparent"
        activeOutlineColor="transparent"
        textColor={theme.colors.onSurface}
        placeholderTextColor={theme.colors.onSurfaceVariant}
        cursorColor={theme.colors.onSurfaceVariant}
        selectionColor={theme.colors.onSurfaceVariant}
        value={text}
        onChangeText={onTextChange}
        maxLength={limit}
      />

      {(hasItems(images) || isImageUploading) && (
        <MediaItemPlaceholderList
          layout="grid"
          columns={3}
          style={styles.imageGrid}
          items={images}
          onDelete={onDeleteImage}
          onReorder={onReorderImages}
          uploading={isImageUploading}
        />
      )}

      <PollPreview
        poll={poll}
        showEdit={showPollEdit}
        onShowEdit={handlePollEdit}
        onChange={onPollChange}
        onDelete={onPollDelete}
      />

      {(hasItems(documents) || isDocumentUploading) && (
        <AttachmentList
          style={{ paddingVertical: 12, paddingHorizontal: 12 }}
          attachments={documents}
          onDelete={onDeleteDocument}
          actionIcon={CloseIcon}
          uploading={isDocumentUploading}
        />
      )}

      <View style={styles.editorFooter}>
        <Text style={styles.counter}>{`${text?.length} / ${limit}`}</Text>

        {isFeatureEnabled(FeaturesEnum.Attachments) && (
          <IconButton
            icon={({ size, color }) => (
              <AttachmentIcon fill={color} width={size} height={size} />
            )}
            mode="contained"
            containerColor={theme.colors.primary}
            iconColor={theme.colors.onPrimary}
            style={styles.iconButton}
            disabled={!canPickMoreAttachments}
            onPress={() => {
              onPickDocument();
            }}
          />
        )}

        {isFeatureEnabled(FeaturesEnum.Poll) && (
          <IconButton
            icon={({ size, color }) => (
              <PollIcon fill={color} width={size} height={size} />
            )}
            mode="contained"
            containerColor={theme.colors.primary}
            iconColor={theme.colors.onPrimary}
            style={styles.iconButton}
            disabled={poll !== undefined}
            onPress={() => handlePollEdit(true)}
          />
        )}

        <MediaMenu
          visible={showImageMenu}
          anchor={
            <IconButton
              icon={({ size, color }) => (
                <CameraIcon fill={color} width={size} height={size} />
              )}
              mode="contained"
              containerColor={theme.colors.primary}
              iconColor={theme.colors.onPrimary}
              style={styles.iconButton}
              disabled={!canPickMoreImages}
              onPress={() => setShowImageMenu(true)}
            />
          }
          canPickImage
          disablePickImage={!canPickMoreImages}
          onPickImage={onPickImage}
          onDismiss={() => setShowImageMenu(false)}
        />
      </View>
    </View>
  );
};
